import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Building2, ShieldCheck, ImagePlus, Inbox, CheckCircle2, Download, Phone, ArrowRight, CalendarClock } from "lucide-react";

const IMG =
  "https://images.pexels.com/photos/8805531/pexels-photo-8805531.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const STEPS = [
  { n: "01", title: "Call or sign up online", body: "Call (586) 306-8449 or click Start free trial below. Create a free facility account in under 2 minutes." },
  { n: "02", title: "Build your profile", body: "Add hours, pricing, 3–5 photos, and check the boxes that describe your home — facility type, care types, specialties, financial options, current availability." },
  { n: "03", title: "Use the 14-day free trial", body: "Your listing goes live immediately — no card needed. See caregiver inquiries flow into your dashboard. If it's not for you, the listing quietly expires." },
  { n: "04", title: "Send $299 by Zelle to activate the year", body: "When you're ready to keep going, send $299 via Zelle to stressfreeaging@gmail.com with your facility name in the memo. We mark you active for 365 days and email a receipt within 1 business day." },
  { n: "05", title: "Receive families", body: "Caregiver inquiries arrive with the family's contact info and notes. Renewal reminders arrive 30, 7, and 1 day before your annual expires." },
];

function ScheduleCallDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ contact_name: "", facility_name: "", contact_phone: "", contact_email: "", best_time: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) return toast.error("Please tell us your name.");
    if (!form.contact_phone.trim()) return toast.error("Please share a phone number so we can call you back.");
    setSubmitting(true);
    try {
      await api.post("/facility-call-requests", form);
      toast.success("We got it — an advisor will call you within 1 business day.");
      setOpen(false);
      setForm({ contact_name: "", facility_name: "", contact_phone: "", contact_email: "", best_time: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again or call us at (586) 306-8449.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent data-testid="schedule-call-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest">Schedule a 15-min intro call</DialogTitle>
          <DialogDescription>
            A Providence advisor will call you to walk you through how membership works and answer any questions — no obligation, no card.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Your name *</label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. Jane Smith" data-testid="call-name" className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Facility name (optional)</label>
            <Input value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} placeholder="e.g. Maple House AFC" data-testid="call-facility" className="mt-1 h-11" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Phone *</label>
              <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(586) 555-0123" data-testid="call-phone" className="mt-1 h-11" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email (optional)</label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="you@facility.com" data-testid="call-email" className="mt-1 h-11" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Best time to reach you</label>
            <Input value={form.best_time} onChange={(e) => setForm({ ...form, best_time: e.target.value })} placeholder="e.g. Weekdays 9–11am, or after 4pm" data-testid="call-besttime" className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Anything we should know?</label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Questions about pricing, listing, photos, payment…" data-testid="call-notes" className="mt-1" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="call-submit" className="w-full rounded-full bg-primary py-6 text-primary-foreground">
              <CalendarClock className="mr-2 h-4 w-4" /> {submitting ? "Sending…" : "Request my 15-min call"}
            </Button>
          </DialogFooter>
          <p className="text-center text-xs text-muted-foreground">Prefer to call now? <a href="tel:+15863068449" className="font-medium text-primary hover:underline">(586) 306-8449</a> — always available 24/7.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ForFacilities() {
  return (
    <div data-testid="for-facilities-page" className="bg-background">
      {/* HERO */}
      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
        <div>
          <p className="text-sm uppercase tracking-[0.18em] text-primary">For AFCs, Assisted Living, Group Homes, Adult Day Centers</p>
          <h1 className="mt-3 font-serif text-5xl text-forest leading-[1.05] sm:text-6xl">
            You have open beds. We have families looking — right now.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">
            Providence Partners is the members-only Adult Day Care &amp; Memory Care Locator — nationwide.
            Working caregivers, hospital discharge planners, and social workers come to us when a family needs
            <strong className="text-forest"> drop-in care, respite, or a trial visit before permanent placement</strong>.
            Every match is routed by a human advisor — so when we send a family your way, they already know your home by name.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground">
              <Link to="/register?role=facility" data-testid="ff-register-btn">
                Start 14-day free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <ScheduleCallDialog>
              <Button variant="outline" className="rounded-full border-emerald/60 bg-emerald/5 px-6 py-6 text-base text-emerald hover:bg-emerald/10" data-testid="ff-schedule-call-btn">
                <CalendarClock className="mr-2 h-4 w-4" /> Schedule a 15-min call
              </Button>
            </ScheduleCallDialog>
            <a href="/providence-partners-facility-membership.pdf" target="_blank" rel="noopener noreferrer" download>
              <Button variant="outline" className="rounded-full px-6 py-6 text-base" data-testid="ff-download-flyer-btn">
                <Download className="mr-2 h-4 w-4" /> Download printable flyer
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Prefer to talk first? <a href="tel:+15863068449" className="font-medium text-primary hover:underline" data-testid="ff-call-link"><Phone className="mb-0.5 mr-1 inline h-3.5 w-3.5" />(586) 306-8449</a> — always available, 24/7.
          </p>
        </div>
        <div className="relative">
          <img src={IMG} alt="Seniors enjoying outdoor activities" className="aspect-[4/5] w-full rounded-2xl object-cover shadow-md" />
        </div>
      </section>

      {/* WHY FACILITIES JOIN */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.18em] text-primary">Why facilities join</p>
          <h2 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">Warm referrals — not lead-mill spam.</h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: <Building2 className="h-5 w-5" />, title: "Fill open beds &amp; day-program slots", body: "Reach caregivers searching this week — not next quarter. Drop-in, respite, and trial-stay tags surface your open capacity instantly." },
            { icon: <Inbox className="h-5 w-5" />, title: "Warm referrals, not cold leads", body: "Every family has spoken with a Providence advisor first. Inquiries arrive with caregiver name, contact info, and care notes." },
            { icon: <ImagePlus className="h-5 w-5" />, title: "Showcase what makes you different", body: "Tag specialties (Memory Care, Secured Memory Care, Parkinson's), accepted payments (Private Pay, VA, Medicaid Waiver, LTC), and real-time availability." },
            { icon: <ShieldCheck className="h-5 w-5" />, title: "Only your team can edit", body: "Your profile is fully editable by your team — nobody else can change your hours, photos, pricing, or availability." },
          ].map((f, i) => (
            <Card key={i} className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid={`ff-benefit-${i}`}>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">{f.icon}</span>
              <h3 className="mt-4 font-serif text-xl text-forest" dangerouslySetInnerHTML={{ __html: f.title }} />
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.18em] text-primary">Membership pricing</p>
          <h2 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">Simple, honest, per location</h2>
          <p className="mt-3 text-base text-muted-foreground">
            Start with a 14-day free trial. No card needed to try the dashboard and upload your first photos.
          </p>
        </div>
        <Card data-testid="pricing-card" className="mt-10 overflow-hidden rounded-3xl border border-emerald/30 bg-gradient-to-br from-emerald/5 to-card p-10 shadow-md sm:p-14">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-block rounded-full bg-gold/15 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-gold">
                Annual membership
              </span>
              <h3 className="mt-4 font-serif text-5xl text-forest">$299<span className="text-2xl text-muted-foreground"> / year</span></h3>
              <p className="mt-2 text-sm text-muted-foreground">Per location · 14-day free trial included</p>
              <ul className="mt-6 grid gap-2 text-sm text-foreground">
                {[
                  "Listed publicly in the Providence Partners locator",
                  "Receive caregiver inquiries directly in your inbox",
                  "Upload 3–5 photos to showcase your home",
                  "Specialty badges: Memory Care, Secured Memory Care, Parkinson's",
                  "Editable by your team only — no other members can change it",
                  "Cancel anytime — no auto-renew surprises",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground" data-testid="pricing-start-trial-btn">
                  <Link to="/register?role=facility">Start 14-day free trial</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full px-6 py-6 text-base">
                  <Link to="/login" data-testid="pricing-login-btn">I already have an account</Link>
                </Button>
              </div>
              <div className="mt-4 rounded-xl border border-gold/40 bg-gold/5 p-4 text-sm" data-testid="pricing-zelle-info">
                <p className="font-medium text-forest">How you pay:</p>
                <p className="mt-1 text-muted-foreground">
                  After your free trial, send <strong className="text-forest">$299</strong> via <strong className="text-forest">Zelle to stressfreeaging@gmail.com</strong> — put your facility name in the memo. We activate your annual membership and email a receipt within 1 business day. No card, no auto-renew — straightforward annual invoicing.
                </p>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="/providence-partners-logo.png"
                alt="Providence Partners crest"
                className="mx-auto h-60 w-60 rounded-full object-cover shadow-md ring-1 ring-emerald/30"
              />
              <p className="mt-4 text-center font-serif text-lg italic text-gold">
                Timely Preparation for Future Eventualities.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* HOW TO JOIN — 5 STEPS */}
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.18em] text-primary">How to join</p>
          <h2 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">From phone call to live listing — under 24 hours.</h2>
        </div>
        <div className="mt-10 grid gap-4">
          {STEPS.map((s) => (
            <Card key={s.n} className="flex gap-5 rounded-2xl border border-border/60 bg-card p-6" data-testid={`ff-step-${s.n}`}>
              <span className="font-serif text-4xl text-gold leading-none">{s.n}</span>
              <div>
                <h3 className="font-serif text-xl text-forest">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* WHAT YOU WILL NOT GET */}
      <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-8">
        <Card className="rounded-3xl border border-gold/40 bg-gold/5 p-10 sm:p-12" data-testid="ff-differentiator">
          <p className="text-sm uppercase tracking-[0.18em] text-gold">What you will not get</p>
          <h3 className="mt-2 font-serif text-3xl text-forest sm:text-4xl">No spam. No paid-per-lead. No tour auto-booking.</h3>
          <p className="mt-4 text-base text-muted-foreground">
            Other directories sell your contact info to every facility on a 50-name list. Providence Partners is the opposite:
            we curate <strong className="text-forest">2–3 vetted matches per family</strong>, and tours are arranged only by a human
            advisor. Your inbox stays clean. Your team's time goes to families who actually want to visit <em>your</em> home.
          </p>
        </Card>
      </section>

      {/* FINAL CTA BAND */}
      <section className="bg-forest text-background">
        <div className="mx-auto max-w-5xl px-6 py-14 text-center lg:px-8">
          <h2 className="font-serif text-3xl text-background sm:text-4xl">Ready to fill your open beds?</h2>
          <p className="mt-3 text-base text-background/80">Start your free trial today — no card needed. Or call us and we'll walk you through it.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button asChild className="rounded-full bg-gold px-6 py-6 text-base text-forest hover:bg-gold/90">
              <Link to="/register?role=facility" data-testid="ff-cta-trial">Start 14-day free trial</Link>
            </Button>
            <ScheduleCallDialog>
              <Button variant="outline" className="rounded-full border-background/40 bg-transparent px-6 py-6 text-base text-background hover:bg-background/10" data-testid="ff-cta-schedule-call">
                <CalendarClock className="mr-2 h-4 w-4" /> Schedule a 15-min call
              </Button>
            </ScheduleCallDialog>
            <a href="tel:+15863068449">
              <Button variant="outline" className="rounded-full border-background/40 bg-transparent px-6 py-6 text-base text-background hover:bg-background/10" data-testid="ff-cta-call">
                <Phone className="mr-2 h-4 w-4" /> (586) 306-8449
              </Button>
            </a>
          </div>
          <p className="mt-6 text-xs text-background/60">
            Serving families nationwide · Network expanding across all 50 states
          </p>
        </div>
      </section>
    </div>
  );
}
